import React, { useEffect, useState } from 'react';
import { authService } from '../../services/authService';
import { AuthPage } from '../UserAuth/AuthPage';
import { StateSyncService } from '../../services/StateSyncService';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isServicesInitialized, setIsServicesInitialized] = useState(false);
  const [initializationTimeout, setInitializationTimeout] = useState<NodeJS.Timeout | null>(null);
  const [initializationError, setInitializationError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // 开发模式下默认跳过登录
  const isDevelopment = process.env.NODE_ENV === 'development';
  const skipAuthInDev = true;

  useEffect(() => {
    // 检查认证状态并初始化服务
    const checkAuthAndInitializeServices = async () => {
      try {
        let authenticated = authService.isAuthenticated();

        // 开发模式下跳过登录
        if (isDevelopment && skipAuthInDev) {
          console.log('开发模式：跳过登录验证');
          authenticated = true;
          setIsAuthenticated(true);
          setIsServicesInitialized(true);
          setIsLoading(false);
          return;
        }

        setIsAuthenticated(authenticated);

        // 如果用户已认证，初始化需要认证的服务
        if (authenticated && !isServicesInitialized) {
          console.log('用户已认证，开始初始化服务...');

          // 设置超时机制，防止初始化时间过长
          const timeout = setTimeout(() => {
            console.warn('服务初始化超时，强制设置为已初始化');
            setIsServicesInitialized(true);
            setIsLoading(false);
          }, 10000); // 10秒超时

          setInitializationTimeout(timeout);

          // 添加重试机制
          const maxRetries = 3;

          const attemptInitialization = async (): Promise<void> => {
            try {
              const stateSyncService = StateSyncService.getInstance();
              await stateSyncService.initializeAuthenticatedServices();

              // 清除超时定时器
              if (timeout) {
                clearTimeout(timeout);
                setInitializationTimeout(null);
              }

              setIsServicesInitialized(true);
              setInitializationError(null);
              console.log('服务初始化完成');
            } catch (error) {
              const currentRetryCount = retryCount + 1;
              console.error(`初始化认证服务失败 (尝试 ${currentRetryCount}/${maxRetries}):`, error);

              setRetryCount(currentRetryCount);
              if (currentRetryCount < maxRetries) {
                console.log(`将在 2 秒后重试...`);
                setTimeout(attemptInitialization, 2000);
                return;
              }

              // 清除超时定时器
              if (timeout) {
                clearTimeout(timeout);
                setInitializationTimeout(null);
              }

              // 即使服务初始化失败，也设置为已初始化，避免无限加载
              console.warn('服务初始化最终失败，强制设置为已初始化');
              setInitializationError('服务初始化失败，但应用将继续运行');
              setIsServicesInitialized(true);
            }
          };

          attemptInitialization();
        }

        setIsLoading(false);
      } catch (error) {
        console.error('检查认证状态失败:', error);
        // 如果检查失败，设置为未认证状态
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    };

    checkAuthAndInitializeServices();

    // 监听存储变化，当登录状态改变时更新
    const handleStorageChange = (event: StorageEvent) => {
      // 如果当前设备已经主动登出，则忽略其他设备的登录事件
      // 使用 sessionStorage 标志来标识当前设备是否已登出
      // sessionStorage 不会跨标签页/设备同步
      const hasLoggedOut = sessionStorage.getItem('wavelink_logged_out');
      if (hasLoggedOut === 'true') {
        console.log('当前设备已登出，忽略 storage 事件');
        return;
      }

      // 只处理 token 相关的变化
      if (event.key === 'token' || event.key === 'user' || event.key === null) {
      checkAuthAndInitializeServices();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // 监听自定义事件，当登录成功或登出时更新状态
    const handleAuthSuccess = async () => {
      console.log('收到登录成功事件，重新检查认证状态...');
      await checkAuthAndInitializeServices();
    };

    const handleAuthLogout = async () => {
      console.log('收到登出事件，重置状态...');

      // 重置UI状态
      setIsAuthenticated(false);
      setIsServicesInitialized(false);
      setIsLoading(false);

      // 注意：MQTT清理已经在 authService.logout() 中处理
      // 这里不需要重复清理，只需要重置UI状态
      console.log('登出事件处理完成，UI状态已重置');
    };

    window.addEventListener('auth-success', handleAuthSuccess);
    window.addEventListener('auth-logout', handleAuthLogout);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-success', handleAuthSuccess);
      window.removeEventListener('auth-logout', handleAuthLogout);

      // 清除超时定时器
      if (initializationTimeout) {
        clearTimeout(initializationTimeout);
      }
    };
  }, [isServicesInitialized]);

  // 如果正在加载，显示加载状态
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <div>正在检查登录状态...</div>
      </div>
    );
  }

  // 如果用户未认证，显示登录页面
  if (!isAuthenticated) {
    return <AuthPage />;
  }

  // 如果用户已认证但服务未初始化，显示加载状态
  if (!isServicesInitialized) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        background: '#667eea'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          border: '4px solid rgba(255, 255, 255, 0.3)',
          borderTop: '4px solid white',
          animation: 'spin 1s linear infinite',
          marginBottom: '20px'
        }}></div>
        <div style={{
          color: 'white',
          fontSize: '16px',
          fontWeight: '500',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          正在初始化服务...
        </div>
        <div style={{
          color: 'rgba(255, 255, 255, 0.8)',
          fontSize: '14px',
          marginTop: '8px'
        }}>
          请稍候，这可能需要几秒钟
        </div>
        {retryCount > 0 && (
          <div style={{
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '12px',
            marginTop: '8px'
          }}>
            重试次数: {retryCount}/3
          </div>
        )}
        {initializationError && (
          <div style={{
            color: '#ff6b6b',
            fontSize: '12px',
            marginTop: '8px',
            textAlign: 'center',
            maxWidth: '300px'
          }}>
            {initializationError}
          </div>
        )}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // 如果用户已认证且服务已初始化，显示子组件
  return <>{children}</>;
};
